"use client";

import { useCookieConsent } from "@jace/insights";
import posthog, { type BootstrapConfig, type PostHogConfig } from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import PostHogPageView from "./pageview";

function PostHogProviderWithoutConsent({
	apiKey,
	bootstrapData,
	children,
	persistence = "localStorage+cookie",
}: {
	apiKey: string;
	bootstrapData?: BootstrapConfig;
	children: React.ReactNode;
	persistence?: PostHogConfig["persistence"];
}) {
	const [isLoading, setIsLoading] = useState(true);
	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want to initialize PostHog once with the API key
	useEffect(() => {
		console.debug("init posthog", apiKey);
		let timeout: ReturnType<typeof setTimeout> | null = null;

		posthog.init(apiKey, {
			api_host: "/ingest",
			ui_host: "https://us.posthog.com",
			bootstrap: bootstrapData,
			capture_pageview: false, // Disable automatic pageview capture, as we capture manually
			capture_pageleave: true, // Enable pageleave capture, because setting capture_pageview: false disables it.
			persistence,
			// Enable debug mode in development
			loaded: (posthog) => {
				if (process.env.NODE_ENV === "development") posthog.debug();
				setIsLoading(false);

				if (timeout) {
					clearTimeout(timeout);
				}
			},
		});

		// PH times out after 5 seconds.
		// This is a fallback
		setTimeout(() => {
			setIsLoading(false);
		}, 4000);

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [apiKey]);

	if (isLoading) {
		return null;
	}

	return (
		<PHProvider client={posthog}>
			<PostHogPageView />
			{children}
		</PHProvider>
	);
}

function PostHogProviderWithConsent({
	apiKey,
	bootstrapData,
	children,
}: {
	apiKey: string;
	bootstrapData?: BootstrapConfig;
	children: React.ReactNode;
}) {
	const { cookieConsent } = useCookieConsent();

	// NOTE: (bwilytsch) We can have the case where posthog hasn't been intialized yet
	useEffect(() => {
		posthog.set_config({
			persistence: cookieConsent?.analytics ? "localStorage+cookie" : "memory",
		});
	}, [cookieConsent?.analytics]);

	return (
		<PostHogProviderWithoutConsent
			apiKey={apiKey}
			bootstrapData={bootstrapData}
			persistence={cookieConsent?.analytics ? "localStorage+cookie" : "memory"}
		>
			{children}
		</PostHogProviderWithoutConsent>
	);
}

export function PostHogProvider({
	consentGiven,
	...props
}: {
	apiKey: string;
	bootstrapData?: BootstrapConfig;
	children: React.ReactNode;
	consentGiven?: boolean;
}) {
	if (consentGiven) {
		return <PostHogProviderWithoutConsent {...props} />;
	}
	return <PostHogProviderWithConsent {...props} />;
}
